import React, { useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../../core/components/InputManager/index.ts'
import { useLookups } from '../../../../global_configs/hooks/useLookups.ts'
import { updateCompanyRecord } from '../../../../../state_manager/state_update_actions/companies/index.ts'
import { AccountRecordFields } from '../../../../../state_manager/data_models/account_record.ts'
import { getCompanyInfoFields } from '../../../utils/fieldConfigs.ts'
import { AccountRecord } from '../../../../../state_manager/data_models/account_record.ts'
import { fetchCompanyById } from '../../../services/companiesService.ts'
import { SECTION_COMPANY_OVERVIEW } from '../../../../../state_manager/constants/strings.ts'
import { buildAccountRoleOptions } from '../../../../../core/utils/accountRoleOptions.ts'

export function CompanyOverviewSection({
  company,
  formData,
  handleFieldChange,
  onPatchSuccess,
  showTitle = true,
  cardProps = {},
  isReadOnly = false,
  recordId = null
}: {
  company?: AccountRecord | null
  formData?: Record<string, any>
  handleFieldChange?: (field: string, value: any) => void
  onPatchSuccess?: (patchRecordId: string, updates: Record<string, any>) => void
  showTitle?: boolean
  cardProps?: Record<string, any>
  isReadOnly?: boolean
  recordId?: string | null
}) {
  const { getLookupOptions } = useLookups()
  const dispatch = useDispatch()

  const accountRoleChoices = useSelector((state: any) => state.globalConfigs?.account_role_choices || [])
  const investorClassifications = useSelector((state: any) => state.globalConfigs?.investor_classifications || [])

  const getRoleOptions = useCallback(
    () => buildAccountRoleOptions(accountRoleChoices),
    [accountRoleChoices]
  )

  const getInvestorClassificationOptions = useCallback(() => {
    return investorClassifications.map((classification: any) => ({
      value: classification.id,
      label: classification.label
    }))
  }, [investorClassifications])

  const data = company || formData || {}

  const companyId =
    recordId || company?.[AccountRecordFields.accounts__id] || data[AccountRecordFields.accounts__id] || data.id

  const helpers = useMemo(
    () => ({
      getLookupOptions: (category: string) => {
        return getLookupOptions(category)
      },
      getRoleOptions,
      getInvestorClassificationOptions
    }),
    [getLookupOptions, getRoleOptions, getInvestorClassificationOptions]
  )

  const handlePatchSuccessLocal = async (patchRecordId: string, updates: Record<string, any>) => {
    if (onPatchSuccess) {
      onPatchSuccess(patchRecordId, updates)
    } else {
      dispatch(
        updateCompanyRecord({
          companyId: patchRecordId,
          updates
        })
      )
    }
  }

  const companyInfoFields = useMemo(() => getCompanyInfoFields(data, helpers), [data, helpers])

  return (
    <FormSection
      data-debug='company-overview-section'
      data-component='CompanyOverviewSection'
      title={SECTION_COMPANY_OVERVIEW}
      titleColor='blue'
      className='shadow-sm border border-gray-200'
      {...cardProps}
    >
      <div data-debug='company-overview-content'>
        <InputManager
          inputMode='read'
          labelPosition='inline'
          fields={companyInfoFields}
          onChange={(key, value) => handleFieldChange?.(key, value)}
          enableAutoPatch={!isReadOnly && !!companyId}
          patchId={companyId}
          recordId={companyId}
          onPatchSuccess={handlePatchSuccessLocal}
          className='company-overview-fields'
          fieldWrapping={true}
          currentRecord={company || data}
        />
      </div>
    </FormSection>
  )
}
